/**
 * What a query said, reduced to the four answers the admin pages care about. A refusal is its own
 * outcome because "the backend refused this viewer" is an answer the page renders, not an error.
 */
import { computed } from '@preact/signals-react';
import type { ReadonlySignal } from '@preact/signals-react';
import type { QueryObserverOptions, QueryObserverResult } from '@tanstack/query-core';
import { isAxiosError } from 'axios';
import type { ReactBindings } from 'src/types/bindings';

/** The statuses that mean "this viewer may not have this": forbidden, or hidden as not found. */
export type RefusalStatus = 403 | 404;

export type Outcome<T> =
    | { status: 'pending' }
    | { status: 'ok'; data: T }
    | { status: 'refused'; httpStatus: RefusalStatus }
    | { status: 'failed'; error: Error };

/** An outcome the page can render from: it either has data or was refused. */
export type Settled<T> = Extract<Outcome<T>, { status: 'ok' | 'refused' }>;

export const PENDING = { status: 'pending' } as const satisfies Outcome<never>;

const refusalStatusOf = (error: Error): RefusalStatus | undefined => {
    if (!isAxiosError(error)) return undefined;
    const status = error.response?.status;
    return status === 403 || status === 404 ? status : undefined;
};

export function outcomeOf<T>(result: QueryObserverResult<T, Error>): Outcome<T> {
    if (result.status === 'success') return { status: 'ok', data: result.data };
    if (result.status === 'error') {
        const httpStatus = refusalStatusOf(result.error);
        return httpStatus === undefined ? { status: 'failed', error: result.error } : { status: 'refused', httpStatus };
    }
    return PENDING;
}

/**
 * Retry policy for admin page requests: an HTTP response, whatever its status, is an answer and is
 * not retried, so a 500 settles at once instead of after a backoff. A request that got no response
 * (network) is retried once.
 */
export const ADMIN_PAGE_RETRY = (failureCount: number, error: Error): boolean =>
    !(isAxiosError(error) && error.response) && failureCount < 1;

/**
 * Observe a query through the model factory and expose it as an {@link Outcome} signal. The
 * observer is disposed with `stack`. Queries get {@link ADMIN_PAGE_RETRY} unless they set their own.
 */
export function observeOutcome<T>(
    stack: DisposableStack,
    queryObserverModelFactory: ReactBindings['QueryObserverModelFactory'],
    options: ReadonlySignal<QueryObserverOptions<T, Error, T>>,
): ReadonlySignal<Outcome<T>> {
    const withRetry = computed(() => ({ retry: ADMIN_PAGE_RETRY, ...options.value }));
    const observer = stack.use(queryObserverModelFactory<T, Error, T>(withRetry));
    return computed(() => outcomeOf(observer.signal.value));
}

type DataOf<O> = Extract<O, { status: 'ok' }> extends { data: infer D } ? D : never;

export type Settlement<O extends Record<string, Outcome<unknown>>> =
    | { status: 'pending' }
    | { status: 'failed'; error: Error }
    | { status: 'settled'; outcomes: { [K in keyof O]: Settled<DataOf<O[K]>> } };

/**
 * Wait for every outcome to settle. A failure wins over pending so a broken request surfaces
 * while other requests are still in flight.
 */
export function settle<O extends Record<string, Outcome<unknown>>>(outcomes: O): Settlement<O> {
    const all = Object.values(outcomes);
    for (const outcome of all) {
        if (outcome.status === 'failed') return { status: 'failed', error: outcome.error };
    }
    if (all.some((outcome) => outcome.status === 'pending')) return { status: 'pending' };
    return { status: 'settled', outcomes: outcomes as { [K in keyof O]: Settled<DataOf<O[K]>> } };
}
